import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_MARK, NAV_LINKS } from "@/lib/site-data";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 md:px-6 py-3 transition-all duration-300 ${
            scrolled
              ? "bg-black/60 backdrop-blur-xl border border-white/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
              : "bg-transparent border border-transparent"
          }`}
        >
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
            <span className="relative inline-flex h-12 w-12 md:h-14 md:w-14 overflow-hidden rounded-full">
              <img
                src={LOGO_MARK}
                alt="Xyro Tech Solutions"
                className="absolute inset-0 h-full w-full object-cover scale-[1.55]"
              />
            </span>
            <span className="font-heading text-white text-xl md:text-2xl font-semibold tracking-tight hidden sm:block">
              Xyro<span className="text-[#5b78ff]"> Tech</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm rounded-full transition-all ${
                    isActive
                      ? "text-white bg-white/[0.06]"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              data-testid="header-book-cta"
              className="hidden md:inline-flex items-center gap-2 btn-primary-xyro rounded-full px-5 py-2.5 text-sm font-medium"
            >
              Book Free Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden p-2 rounded-full bg-white/[0.04] border border-white/[0.08] text-white"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="mobile-menu"
              className="lg:hidden mt-3 rounded-2xl bg-black/80 backdrop-blur-xl border border-white/[0.08] p-4 relative z-[1000]"
            >
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl text-base ${
                      isActive ? "bg-white/[0.06] text-white" : "text-zinc-300 hover:bg-white/[0.04]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-2 inline-flex w-full justify-center items-center gap-2 btn-primary-xyro rounded-full px-5 py-3 text-sm font-medium"
              >
                Book Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
